// src/contexts/AuthContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  onAuthStateChanged,
  signInWithPopup,
  GoogleAuthProvider,
  signOut
} from 'firebase/auth';
import { auth } from '../config/firebase';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// eslint-disable-next-line max-lines-per-function
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [customClaims, setCustomClaims] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  useEffect(() => {
    // Set up auth state listener
    const unsubscribe = onAuthStateChanged(
      auth,
      async (currentUser) => {
        setUser(currentUser);

        // Get custom claims from ID token
        if (currentUser) {
          try {
            const idTokenResult = await currentUser.getIdTokenResult();
            setCustomClaims(idTokenResult.claims || {});
          } catch (err) {
            console.error('Failed to get ID token result:', err);
            setCustomClaims({});
          }
        } else {
          setCustomClaims({});
        }

        setIsLoading(false);
        setError(null);
      },
      (err) => {
        console.error('Auth state change error:', err);
        setError(err.message);
        setCustomClaims({});
        setIsLoading(false);
      }
    );

    // Clean up subscription on unmount
    return () => unsubscribe();
  }, []);

  // Set up periodic token refresh (every 5 minutes)
  useEffect(() => {
    if (!user) {
      return; // No user, no need for refresh
    }

    let intervalId;

    // Initial delay to avoid immediate refresh after login
    const initialDelay = setTimeout(() => {
      // Set up interval for periodic refresh
      intervalId = setInterval(async () => {
        try {
          // Force token refresh
          await user.getIdToken(true);

          // Get updated claims
          const idTokenResult = await user.getIdTokenResult();
          setCustomClaims(idTokenResult.claims || {});
        } catch (err) {
          console.error('Failed to refresh token during interval:', err);
        }
      }, 5 * 60 * 1000); // 5 minutes
    }, 30 * 1000); // Wait 30 seconds before starting interval

    // Cleanup function
    return () => {
      clearTimeout(initialDelay);
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [user]); // Re-run when user changes

  // Sign in with Google
  // Uses redirect for custom domains (more reliable) and popup for localhost
  const login = async () => {
    try {
      setIsLoading(true);
      const provider = new GoogleAuthProvider();

      // Advisor app allows any Google domain - no hd restriction
      console.log('Advisor app: No domain restriction');

      // Always use popup sign-in. signInWithRedirect breaks on browsers
      // with third-party cookie partitioning (Firefox, Zen, Safari) because
      // Firebase stores redirect state in a cross-origin iframe cookie.
      console.log('Using popup sign-in');
      const result = await signInWithPopup(auth, provider);
      setError(null);
      return result;
    } catch (err) {
      console.error('Login failed:', err);
      console.error('Error code:', err.code);
      console.error('Error message:', err.message);
      setError(err.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  // Sign out
  const logout = async () => {
    try {
      setIsLoading(true);
      await signOut(auth);
      setError(null);
    } catch (err) {
      console.error('Logout failed:', err);
      setError(err.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  // Get ID token for API calls
  const getIdToken = async () => {
    try {
      if (!user) {
        throw new Error('User not authenticated');
      }
      return await user.getIdToken();
    } catch (err) {
      console.error('Failed to get ID token:', err);
      setError(err.message);
      throw err;
    }
  };

  // Force refresh token to get latest claims
  const refreshToken = async () => {
    try {
      if (!user) {
        throw new Error('User not authenticated');
      }

      // Force token refresh
      const newToken = await user.getIdToken(true);

      // Get updated claims
      const idTokenResult = await user.getIdTokenResult();
      setCustomClaims(idTokenResult.claims || {});

      return newToken;
    } catch (err) {
      console.error('Failed to refresh token:', err);
      setError(err.message);
      throw err;
    }
  };

  // Check if user is authenticated
  const isAuthenticated = !!user;

  // Value object with all auth-related functionality
  const value = {
    user,
    customClaims,
    isLoading,
    error,
    isAuthenticated,
    login,
    logout,
    getIdToken,
    refreshToken,
    // Convenience properties for common claims from JWT
    // These are set server-side in the JWT token and should be used for authorization
    // DO NOT make API calls to check these - they're already in the token
    isPlatformAdmin: customClaims.isPlatformAdmin || false,
    isWorkspaceAdmin: customClaims.isWorkspaceAdmin || false,
    hasFgosAccess: customClaims.hasFgosAccess || false
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};